import React, { useState } from 'react'
import axios from 'axios'
import {
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Chip,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Grid,
  Tooltip,
  Checkbox,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
  Edit as EditIcon,
  ArrowForward as ArrowForwardIcon,
  MergeType as MergeIcon,
  DeleteOutline as DeleteOutlineIcon
} from '@material-ui/icons'

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2)
  },
  card: {
    marginBottom: theme.spacing(2),
    transition: 'box-shadow 150ms ease-in-out, transform 150ms ease-in-out',
    '&:hover': {
      boxShadow: theme.shadows[4]
    }
  },
  actionArea: {
    height: '100%',
    '&:hover $viewItemsHint': {
      opacity: 1
    }
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  nameText: {
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1)
  },
  metaLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  viewItemsHint: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 12,
    color: theme.palette.primary.main,
    opacity: 0.6,
    transition: 'opacity 150ms ease-in-out'
  }
}))

const GROUP_TYPE_OPTIONS = [
  { value: 'base', label: 'BASE' },
  { value: 'alternate', label: 'ALT' },
  { value: 'allowance_group', label: 'ALLOW' },
  { value: 'unknown', label: 'UNKNOWN' }
]

const getChipProps = (groupType) => {
  switch (groupType) {
    case 'base':
      return { label: 'BASE', color: 'primary', style: {} }
    case 'alternate':
      return { label: 'ALT', color: 'default', style: { backgroundColor: '#4caf50', color: '#fff' } }
    case 'allowance_group':
      return { label: 'ALLOW', color: 'default', style: { backgroundColor: '#ff9800', color: '#fff' } }
    default:
      return { label: 'UNKNOWN', color: 'default', style: {} }
  }
}

const GroupCard = ({ group, onGroupUpdate, onGroupClick, url }) => {
  const classes = useStyles()
  const [editing, setEditing] = useState(false)
  const [nameValue, setNameValue] = useState(group.name || '')
  const [saving, setSaving] = useState(false)

  const chipProps = getChipProps(group.groupType)

  const saveName = () => {
    const trimmed = nameValue.trim()
    if (!trimmed || trimmed === group.name) {
      setEditing(false)
      setNameValue(group.name || '')
      return
    }
    setSaving(true)
    axios.put(`${url}/api/bid-estimation/bid-groups/${group._id}`, { name: trimmed })
      .then(() => {
        setSaving(false)
        setEditing(false)
        onGroupUpdate()
      })
      .catch(err => {
        console.error('Failed to save group name:', err)
        setSaving(false)
        setEditing(false)
        setNameValue(group.name || '')
      })
  }

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      saveName()
    } else if (e.key === 'Escape') {
      setEditing(false)
      setNameValue(group.name || '')
    }
  }

  const handleTypeChange = (e) => {
    const newType = e.target.value
    axios.put(`${url}/api/bid-estimation/bid-groups/${group._id}`, { groupType: newType })
      .then(() => {
        onGroupUpdate()
      })
      .catch(err => {
        console.error('Failed to save group type:', err)
      })
  }

  // Stop clicks on editable controls from triggering the card's drill-in handler.
  const stop = (e) => e.stopPropagation()

  const handleCardClick = () => {
    if (editing) return
    if (onGroupClick) onGroupClick(group._id)
  }

  return (
    <Card className={classes.card} variant="outlined">
      <Tooltip title="Click to view this group's items" placement="top" arrow>
        <CardActionArea
          className={classes.actionArea}
          onClick={handleCardClick}
          disabled={editing}
        >
          <CardContent>
            <div className={classes.cardHeader} onClick={stop}>
              <Chip
                label={chipProps.label}
                color={chipProps.color}
                size="small"
                style={chipProps.style}
              />
              <Select
                value={group.groupType || 'unknown'}
                onChange={handleTypeChange}
                onClick={stop}
                disableUnderline
                style={{ fontSize: 12, color: '#888' }}
              >
                {GROUP_TYPE_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </div>

            <div className={classes.nameRow} onClick={stop}>
              {editing ? (
                <TextField
                  value={nameValue}
                  onChange={e => setNameValue(e.target.value)}
                  onBlur={saveName}
                  onKeyDown={handleNameKeyDown}
                  autoFocus
                  disabled={saving}
                  size="small"
                  variant="outlined"
                  style={{ minWidth: 240 }}
                />
              ) : (
                <>
                  <Typography
                    variant="subtitle1"
                    className={classes.nameText}
                    onClick={(e) => { e.stopPropagation(); setEditing(true) }}
                  >
                    {group.name || '(unnamed)'}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setEditing(true) }}
                    aria-label="Edit group name"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </div>

            <div className={classes.metaRow}>
              <div className={classes.metaLeft}>
                {group.sourceSheet && (
                  <Typography variant="caption" color="textSecondary">
                    Sheet: {group.sourceSheet}
                  </Typography>
                )}
                <Typography variant="caption" color="textSecondary">
                  {group.itemCount != null ? group.itemCount : (group.items ? group.items.length : 0)} items
                </Typography>
              </div>
              <span className={classes.viewItemsHint}>
                View items <ArrowForwardIcon style={{ fontSize: 14 }} />
              </span>
            </div>
          </CardContent>
        </CardActionArea>
      </Tooltip>
    </Card>
  )
}

const GroupsTab = ({ groups, onGroupUpdate, onGroupClick, url }) => {
  const classes = useStyles()
  const [selected, setSelected] = useState([])
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [moveItemsTo, setMoveItemsTo] = useState('')

  const handleSelectGroup = (groupId) => {
    setSelected(prev =>
      prev.includes(groupId) ? prev.filter(x => x !== groupId) : [...prev, groupId]
    )
  }

  const handleMergeGroups = async () => {
    if (selected.length < 2) return
    try {
      await axios.post(`${url}/api/bid-estimation/bid-groups/merge`, { groupIds: selected })
      setSelected([])
      onGroupUpdate()
    } catch (e) {
      console.error('Merge groups failed:', e)
      alert('Merge failed: ' + (e.response?.data?.error || e.message))
    }
  }

  const handleDeleteGroup = async (withItems) => {
    if (!deleteTarget) return
    try {
      const params = !withItems && moveItemsTo ? `?moveItemsTo=${moveItemsTo}` : ''
      await axios.delete(`${url}/api/bid-estimation/bid-groups/${deleteTarget._id}${params}`)
      setDeleteTarget(null)
      setMoveItemsTo('')
      setSelected([])
      onGroupUpdate()
    } catch (e) {
      console.error('Delete group failed:', e)
      alert('Delete failed: ' + (e.response?.data?.error || e.message))
    }
  }

  if (!groups || groups.length === 0) {
    return (
      <div className={classes.root}>
        <Typography color="textSecondary">No groups detected</Typography>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      {/* Bulk action toolbar */}
      {selected.length > 0 && (
        <Paper style={{
          padding: '8px 16px',
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          backgroundColor: '#e3f2fd'
        }}>
          <Typography variant='body2' style={{ fontWeight: 600 }}>
            {selected.length} group{selected.length !== 1 ? 's' : ''} selected
          </Typography>
          <Button size='small' startIcon={<MergeIcon />} onClick={handleMergeGroups}
            disabled={selected.length < 2} variant='outlined'>
            Merge Groups
          </Button>
          {selected.length === 1 && (
            <Button size='small' startIcon={<DeleteOutlineIcon />}
              onClick={() => setDeleteTarget(groups.find(g => g._id === selected[0]))}
              variant='outlined' color='secondary'>
              Delete Group
            </Button>
          )}
          <Button size='small' onClick={() => setSelected([])}>Clear</Button>
        </Paper>
      )}

      <Grid container spacing={2}>
        {groups.map(group => (
          <Grid item xs={12} sm={6} md={4} key={group._id}>
            <div style={{ position: 'relative' }}>
              <Checkbox
                checked={selected.includes(group._id)}
                onChange={() => handleSelectGroup(group._id)}
                style={{ position: 'absolute', top: 4, left: 4, zIndex: 1 }}
                size='small'
              />
              <GroupCard
                group={group}
                onGroupUpdate={onGroupUpdate}
                onGroupClick={onGroupClick}
                url={url}
              />
            </div>
          </Grid>
        ))}
      </Grid>

      {/* Delete group confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth='xs' fullWidth>
        <DialogTitle>Delete Group: {deleteTarget?.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This group has {deleteTarget?.itemCount || deleteTarget?.items?.length || 0} items.
          </DialogContentText>
          {groups.length > 1 && (
            <FormControl fullWidth size='small' variant='outlined' style={{ marginTop: 12 }}>
              <InputLabel>Move items to</InputLabel>
              <Select value={moveItemsTo} onChange={e => setMoveItemsTo(e.target.value)}
                label='Move items to'>
                {groups.filter(g => g._id !== deleteTarget?._id).map(g => (
                  <MenuItem key={g._id} value={g._id}>{g.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          {moveItemsTo && (
            <Button onClick={() => handleDeleteGroup(false)} color='primary' variant='outlined'>
              Move Items & Delete Group
            </Button>
          )}
          <Button onClick={() => handleDeleteGroup(true)} color='secondary' variant='contained'>
            Delete Group & All Items
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default GroupsTab
